import React, { useState } from 'react';
import { ChevronRight, ChevronDown, CheckCircle, AlertTriangle, Info, GitMerge } from 'lucide-react';
import type { QualityScore, PipelinePhase, Issue, Question, CrossUnitFindings } from './types';
import IssueStatusBadge from './IssueStatusBadge';

interface OverviewTabProps {
  qualityScore: QualityScore;
  summary: string;
  stats: Record<string, any>;
  pipelinePhases: PipelinePhase[];
  issues: Issue[];
  questions: Question[];
  onNavigateToIssue: (issueId: string) => void;
  onNavigateToQuestion: (questionId: string) => void;
  isProse?: boolean;
  crossUnitFindings?: CrossUnitFindings | null;
  onOpenReconciliation?: () => void;
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-[#0F172A]">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="px-1 py-0.5 rounded bg-[#F1F5F9] font-mono text-[12px]">{part.slice(1, -1)}</code>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function renderMarkdownSummary(summary: string): React.ReactNode {
  if (!summary) return <p className="text-[#94A3B8] italic">No summary available.</p>;
  const paragraphs = summary.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  return paragraphs.map((para, i) => (
    <p key={i}>{renderInlineMarkdown(para)}</p>
  ));
}

const QualityRing: React.FC<{ score: number; label: string; color: string }> = ({ score, label, color }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 96 96" className="w-24 h-24">
          <circle cx="48" cy="48" r={radius} fill="none" stroke="#F1F5F9" strokeWidth="7" />
          <circle
            cx="48" cy="48" r={radius} fill="none"
            stroke={color} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            transform="rotate(-90 48 48)"
            style={{ transition: 'stroke-dashoffset 1s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[22px] font-bold" style={{ color }}>{score}%</span>
          <span className="text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider">Quality</span>
        </div>
      </div>
      <span className="text-[12px] font-semibold text-[#64748B]">{label}</span>
    </div>
  );
};

const SubScore: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div className="flex flex-col items-center gap-1">
    <div className="w-full h-[4px] bg-[#F1F5F9] rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color, transition: 'width 1s' }} />
    </div>
    <span className="text-[10px] text-[#94A3B8] font-medium">{label}</span>
    <span className="text-[12px] font-bold" style={{ color }}>{value}%</span>
  </div>
);

const ReconStat: React.FC<{ label: string; value: number; sub?: string; tone: 'violet' | 'amber' | 'red' | 'blue' }> = ({ label, value, sub, tone }) => {
  const toneClass = tone === 'violet' ? 'text-[#5B21B6]' : tone === 'amber' ? 'text-[#92400E]' : tone === 'red' ? 'text-[#991B1B]' : 'text-[#1E40AF]';
  return (
    <div className="bg-white rounded border border-[#E2E8F0] px-3 py-2">
      <div className={`text-[20px] font-bold tabular-nums ${toneClass}`}>{value.toLocaleString()}</div>
      <div className="text-[11px] text-[#64748B] mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-[#94A3B8] mt-0.5">{sub}</div>}
    </div>
  );
};

const OverviewTab: React.FC<OverviewTabProps> = ({
  qualityScore, summary, stats, pipelinePhases, issues, questions, onNavigateToIssue, onNavigateToQuestion, isProse = false,
  crossUnitFindings = null, onOpenReconciliation,
}) => {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);
  const beforeColor = qualityScore.before >= 70 ? '#059669' : qualityScore.before >= 40 ? '#F59E0B' : '#EF4444';
  const afterColor = qualityScore.after >= 70 ? '#059669' : qualityScore.after >= 40 ? '#F59E0B' : '#EF4444';
  const delta = qualityScore.after - qualityScore.before;

  const openIssueCount = issues.filter(i => i.status === 'AWAITING_DECISION' || i.status === 'CANNOT_RESOLVE').length;
  const appliedQCount = questions.filter(q => q.applied || q.status === 'APPLIED').length;

  return (
    <div className="space-y-6">
      {isProse && (
        <div className="flex items-start gap-2.5 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg px-4 py-3">
          <Info className="w-4 h-4 text-[#1D4ED8] mt-0.5 flex-shrink-0" />
          <div className="text-[12px] text-[#1E3A8A] leading-relaxed">
            <span className="font-semibold">How the document quality score works.</span> The <em>Before</em> score is a snapshot of the document's quality at scan time. As you resolve stakeholder questions in the <span className="font-semibold">Questions</span> tab — picking winning values, filling gaps, rewriting sections, or acknowledging findings — the <em>After</em> score moves up to reflect the resolutions. Applied fixes (edit/fill/merge/replace) actually modify the document text and are saved to the audit trail; pick-winner decisions propagate the chosen value to every conflicting section.
          </div>
        </div>
      )}
      {crossUnitFindings && (crossUnitFindings.rollup?.relationships_inferred || 0) > 0 && (
        <div className="border border-[#C4B5FD] bg-[#F5F3FF] rounded-lg px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <GitMerge className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-[13px] font-semibold text-[#5B21B6]">Cross-Sheet Reconciliation</span>
              {crossUnitFindings.degraded && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FEF3C7] text-[#92400E] uppercase">Degraded</span>
              )}
            </div>
            {onOpenReconciliation && (
              <button onClick={onOpenReconciliation} className="text-[12px] font-medium text-[#7C3AED] hover:underline">
                Open Reconciliation tab →
              </button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3 mt-3">
            <ReconStat label="Relationships" value={crossUnitFindings.rollup.relationships_inferred} tone="violet" />
            <ReconStat label="Orphan rows" value={crossUnitFindings.rollup.orphans_total} sub={`${crossUnitFindings.rollup.orphans_findings} findings`} tone="amber" />
            <ReconStat label="Master violations" value={crossUnitFindings.rollup.master_violations_total} sub={`${crossUnitFindings.rollup.master_violations_findings} findings`} tone="red" />
            <ReconStat label="Vendor clusters" value={crossUnitFindings.rollup.vendor_clusters_proposed} sub={`${crossUnitFindings.rollup.compound_violations_findings} compound rules`} tone="blue" />
          </div>
        </div>
      )}
      <div className="grid grid-cols-3 border border-[#E2E8F0] rounded-xl overflow-hidden">
        <div className="bg-white p-5 flex flex-col items-center gap-3">
          <QualityRing score={qualityScore.before} label="Before Cleaning" color={beforeColor} />
        </div>

        {isProse ? (
          (() => {
            const documentsReviewed = Number(stats?.documentsReviewed) || 0;
            const sectionsExtracted = Number(stats?.sectionsExtracted) || 0;
            const totalIssues = Number(stats?.issuesFound) || (Array.isArray(issues) ? issues.length : 0);
            const totalQuestions = Number(stats?.questionsRaised) || (Array.isArray(questions) ? questions.length : 0);
            const answeredQuestions = Array.isArray(questions) ? questions.filter(q => q.applied || q.status === 'APPLIED').length : 0;
            const openQuestions = Math.max(0, totalQuestions - answeredQuestions);
            return (
              <div className="bg-[#FAFCFF] p-5 flex flex-col items-center justify-center border-x border-[#E2E8F0] text-center">
                <span className="text-[32px] font-bold text-[#0D9488] leading-none">{totalIssues}</span>
                <span className="text-[12px] text-[#64748B] font-medium mt-1">Issues identified</span>
                <span className="text-[11px] text-[#0D9488] font-semibold mt-2">
                  {answeredQuestions} of {totalQuestions} stakeholder question{totalQuestions === 1 ? '' : 's'} answered
                </span>
                <span className="text-[10px] text-[#94A3B8] mt-1">
                  {sectionsExtracted} section{sectionsExtracted === 1 ? '' : 's'} · {documentsReviewed} document{documentsReviewed === 1 ? '' : 's'}
                </span>
                {openQuestions > 0 && (
                  <span className="text-[10px] text-[#92400E] font-semibold mt-1">{openQuestions} awaiting review</span>
                )}
              </div>
            );
          })()
        ) : (() => {
          const displayedDelta = Math.max(0, delta);
          const isReevaluation = !!qualityScore.revisedDown || (qualityScore.after === qualityScore.before && (Number(stats?.qualityScoreAfter) || 0) < (Number(stats?.qualityScoreBefore) || 0));
          const reevaluationTooltip = 'Phase 2 review found issues that the initial scan underestimated. The lower score replaces the optimistic Before estimate; no degradation actually occurred.';
          return (
            <div className="bg-[#FAFCFF] p-5 flex flex-col items-center justify-center border-x border-[#E2E8F0]">
              <span className="text-[32px] font-bold text-[#0D9488] leading-none">
                {displayedDelta > 0 ? '+' : ''}{displayedDelta}%
              </span>
              <span className="text-[12px] text-[#64748B] font-medium mt-1">Improvement</span>
              {isReevaluation && (
                <span
                  className="mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEF3C7] text-[#92400E] cursor-help"
                  title={reevaluationTooltip}
                >
                  Re-evaluated by Phase 2 review
                </span>
              )}
              {stats.totalRows && (
                <span className="text-[11px] text-[#0D9488] font-semibold mt-2">
                  {(stats.rowsAfterCleaning || stats.totalRows).toLocaleString()} records processed
                </span>
              )}
            </div>
          );
        })()}

        <div className="bg-white p-5 flex flex-col items-center gap-3">
          <QualityRing score={qualityScore.after} label="After Cleaning" color={afterColor} />
        </div>
      </div>

      {!isProse && (
        <div className="border border-[#E2E8F0] rounded-xl px-5 py-3">
          <div className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-2">Quality breakdown (post-cleaning)</div>
          <div className="grid grid-cols-3 gap-4">
            <SubScore label="Completeness" value={Math.round(qualityScore.completeness)} color={afterColor} />
            <SubScore label="Consistency" value={Math.round(qualityScore.consistency)} color={afterColor} />
            <SubScore label="Recon Rate" value={Math.round(qualityScore.validity)} color={afterColor} />
          </div>
        </div>
      )}

      <div className="bg-[#FAFCFF] border border-[#E2E8F0] rounded-xl px-5 py-4">
        <h3 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-2">Executive Summary</h3>
        <div className="text-[13px] text-[#0F172A] leading-relaxed space-y-2">
          {renderMarkdownSummary(summary)}
        </div>
      </div>

      {pipelinePhases.length > 0 && (
        <div>
          <h3 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-3">Pipeline Phases</h3>
          <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
            {pipelinePhases.map((phase) => {
              const isOpen = expandedPhase === phase.phase_number;
              const isPartial = phase.status === 'partial';
              return (
                <div key={phase.phase_number} className="border-b border-[#E2E8F0] last:border-b-0">
                  <button
                    onClick={() => setExpandedPhase(isOpen ? null : phase.phase_number)}
                    className={`w-full grid grid-cols-[28px_220px_1fr_auto_28px] items-start gap-0 text-left hover:bg-[#FAFCFF] transition-colors ${isOpen ? 'bg-[#FAFCFF]' : ''}`}
                  >
                    <div className="flex items-center justify-center py-4 pl-4">
                      <div className={`w-2.5 h-2.5 rounded-full ${phase.status === 'complete' ? 'bg-[#0D9488]' : 'bg-[#F59E0B]'}`} />
                    </div>
                    <div className="py-4 px-3 border-r border-[#E2E8F0]">
                      <p className="text-[13px] font-semibold text-[#0F172A]">Phase {phase.phase_number} &mdash; {phase.name}</p>
                      {phase.sub_label && (
                        <p className="text-[10px] text-[#94A3B8] font-medium uppercase tracking-wider mt-0.5">{phase.sub_label}</p>
                      )}
                    </div>
                    <div className="py-4 px-4">
                      <p className="text-[13px] text-[#0F172A] leading-relaxed">{phase.narrative}</p>
                      {phase.group_stats && phase.group_stats.length > 0 && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                          {phase.group_stats.map((gs, i) => {
                            const dot = gs.tone === 'green' ? 'bg-[#0D9488]' : gs.tone === 'amber' ? 'bg-[#F59E0B]' : 'bg-[#3B82F6]';
                            return (
                              <div key={i} className="flex items-center gap-1.5 text-[11px]">
                                <div className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                                <span className="font-semibold text-[#0F172A]">{gs.count.toLocaleString()}</span>
                                <span className="text-[#64748B]">{gs.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="py-4 px-2 flex items-center gap-2">
                      {isPartial && phase.pending_count ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FEF3C7] text-[#92400E]">
                          <AlertTriangle className="w-2.5 h-2.5" />
                          {phase.pending_count} Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#D1FAE5] text-[#065F46]">
                          Complete
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-center py-4 pr-3">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-[#94A3B8]" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8]" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 pt-2 bg-[#FAFCFF] border-t border-[#E2E8F0]">
                      {phase.detail_cards && phase.detail_cards.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                          {phase.detail_cards.map((card, ci) => (
                            <div key={ci} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                              <h5 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-3">{card.title}</h5>
                              <div className="space-y-2">
                                {card.items.map((item, i) => {
                                  const iconEl = item.icon === 'check'
                                    ? <CheckCircle className="w-3.5 h-3.5 text-[#059669] flex-shrink-0 mt-0.5" />
                                    : item.icon === 'warning'
                                      ? <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                                      : <Info className="w-3.5 h-3.5 text-[#3B82F6] flex-shrink-0 mt-0.5" />;
                                  return (
                                    <div key={i} className="flex items-start gap-2 text-[12px]">
                                      {iconEl}
                                      <span className={`leading-snug ${item.icon === 'warning' ? 'text-[#92400E]' : 'text-[#0F172A]'}`}>{item.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {phase.sub_items && phase.sub_items.length > 0 && (
                        <div className="space-y-1.5 mt-3">
                          {phase.sub_items.map((item, i) => {
                            const iconEl = item.icon === 'check'
                              ? <CheckCircle className="w-3.5 h-3.5 text-[#059669] flex-shrink-0" />
                              : item.icon === 'warning'
                                ? <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0" />
                                : <Info className="w-3.5 h-3.5 text-[#3B82F6] flex-shrink-0" />;
                            return (
                              <div key={i} className="flex items-center gap-2 text-[12px]">
                                {iconEl}
                                <span className="text-[#0F172A]">{item.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-3 mt-4">
                        <div className="flex items-center gap-2 text-[12px] bg-[#F0FDF4] rounded-lg px-3 py-2 border border-[#D1FAE5]">
                          <div className="w-2 h-2 rounded-full bg-[#059669] flex-shrink-0" />
                          <span className="text-[#0F172A] font-semibold">{phase.fixed_count.toLocaleString()}</span>
                          <span className="text-[#64748B]">Fixed</span>
                        </div>
                        <div className="flex items-center gap-2 text-[12px] bg-[#FEF3C7] rounded-lg px-3 py-2 border border-[#FDE68A]">
                          <div className="w-2 h-2 rounded-full bg-[#F59E0B] flex-shrink-0" />
                          <span className="text-[#0F172A] font-semibold">{phase.flagged_count.toLocaleString()}</span>
                          <span className="text-[#64748B]">Flagged</span>
                        </div>
                        <div className="flex items-center gap-2 text-[12px] bg-[#F1F5F9] rounded-lg px-3 py-2 border border-[#E2E8F0]">
                          <div className="w-2 h-2 rounded-full bg-[#94A3B8] flex-shrink-0" />
                          <span className="text-[#0F172A] font-semibold">{phase.skipped_count.toLocaleString()}</span>
                          <span className="text-[#64748B]">Skipped</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-[#E2E8F0] rounded-xl px-5 py-4">
          <h3 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-3">
            Issues <span className="text-[#94A3B8] font-normal">({openIssueCount} open)</span>
          </h3>
          <div className="space-y-2">
            {issues.slice(0, 6).map(issue => (
              <button
                key={issue.issue_id}
                onClick={() => onNavigateToIssue(issue.issue_id)}
                className="w-full flex items-center gap-2 text-left hover:bg-[#F8FAFC] rounded-lg px-2 py-1.5 transition-colors"
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  issue.severity === 'HIGH' ? 'bg-[#EF4444]' : issue.severity === 'MEDIUM' ? 'bg-[#F59E0B]' : 'bg-[#6366F1]'
                }`} />
                <span className="text-[12px] text-[#0F172A] flex-1 truncate">{issue.summary || issue.business_explanation}</span>
                <code className="text-[10px] text-[#94A3B8] font-mono flex-shrink-0">{issue.column}</code>
                <IssueStatusBadge status={issue.status} />
              </button>
            ))}
          </div>
        </div>

        <div className="border border-[#E2E8F0] rounded-xl px-5 py-4">
          <h3 className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider mb-3">
            Questions <span className="text-[#94A3B8] font-normal">({appliedQCount}/{questions.length} answered)</span>
          </h3>
          <div className="w-full h-2 bg-[#F1F5F9] rounded-full overflow-hidden mb-2">
            <div className="h-full bg-[#0D9488] rounded-full transition-all duration-500" style={{ width: `${questions.length > 0 ? (appliedQCount / questions.length) * 100 : 0}%` }} />
          </div>
          {questions.length > appliedQCount && (
            <p className="text-[11px] text-[#0D9488] font-medium mb-3">
              &uarr; {Math.max(1, Math.round((questions.length - appliedQCount) * 2))}% more improvement available when all questions answered
            </p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {questions.map((q, i) => {
              const isApplied = q.applied || q.status === 'APPLIED';
              return (
                <button
                  key={q.question_id}
                  onClick={() => onNavigateToQuestion(q.question_id)}
                  className={`w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center transition-colors ${
                    isApplied ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#FEF3C7] text-[#92400E]'
                  } hover:ring-2 hover:ring-[#0D9488] hover:ring-offset-1`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
